import { Label } from '@/components/ui/label.tsx'
import { useCallback, useMemo, useState } from 'react'

import { Input } from '@/components/ui/input.tsx'
import { cn } from '@/lib/utils.ts'

import { useMutateIteration } from '@/requests/mutations/useMutateIteration.ts'
import { formatDateRange } from '@/utils/date.ts'

type Props = {
  number: number
  velocity: number
  acceptedPoints?: number
  start: number
  finish: number
  teamStrength: number
  stories: Story[]
  isDraggableStories?: boolean
  type?: 'generic' | 'done' | 'currentIteration'
  isOpen?: boolean
  toggleAccordion?: () => void
}

const commonOverlayStyle =
  'relative rounded-xs h-6 overflow-hidden after:bg-stateLayer-onDark-hovered after:opacity-12 after:absolute after:inset-0'

export const IterationDividerLabel = ({
  number,
  velocity,
  acceptedPoints,
  start,
  finish,
  teamStrength: initialTeamStrength,
  stories,
  type = 'generic',
  isOpen,
  toggleAccordion,
}: Props) => {
  const [isTeamStrengthEditMode, setIsTeamStrengthEditMode] = useState(false)
  const [teamStrength, setTeamStrength] = useState<string>((initialTeamStrength * 100).toString())
  const { mutate } = useMutateIteration()

  const focusTeamStrengthInput = useCallback(() => {
    ;(document.querySelector(`#team-strength-${number}`) as HTMLInputElement).focus()
  }, [number])

  const updateTeamStrength = useCallback(() => {
    const value = Number.parseInt(teamStrength, 10)
    if (Number.isNaN(value) || value < 0 || value > 1000) {
      focusTeamStrengthInput()
      return
    }
    mutate(number, value / 100).finally(() => setIsTeamStrengthEditMode(false))
  }, [number, teamStrength, mutate, focusTeamStrengthInput])

  const points = useMemo(() => {
    switch (type) {
      case 'done':
        return `${acceptedPoints} points`
      case 'currentIteration':
        return `${acceptedPoints} of ${velocity} points`
      default:
        return `${velocity} points`
    }
  }, [type, acceptedPoints, velocity])

  const getDividerColor = () => {
    switch (type) {
      case 'done':
        return 'bg-surface-iterationMarker-done'
      case 'currentIteration':
        return 'bg-surface-iterationMarker-backlog'
      default:
        return 'bg-surface-iterationMarker-backlog'
    }
  }

  return (
    <div
      key={number}
      aria-label="iteration divider"
      className={cn('flex-1 flex justify-between gap-4 p-2 mb-[1px]', getDividerColor())}
    >
      <div className={cn('flex gap-4')}>
        {type === 'currentIteration' ? (
          <div className="flex gap-1 items-center">
            <div className="flex justify-center items w-5 h-5">
              <img src="/images/calendar-icon.svg" alt="current" />
            </div>
            <p className="font-EN-KR-caption-bold text-contents-onDarkPrimary">{points}</p>
          </div>
        ) : (
          <div className="flex gap-1 items-center">
            {/* biome-ignore lint/a11y/useKeyWithClickEvents: */}
            <div
              className={cn(
                'bg-surface-onColorHighlight rounded-xs w-5 h-5 flex justify-center items-center overflow-hidden',
                stories.length > 0 &&
                  'cursor-pointer relative hover:after:bg-stateLayer-onDark-hovered hover:after:opacity-12 after:absolute after:inset-0 after:pointer-events-none',
              )}
              onClick={() => {
                if (stories.length === 0) return
                toggleAccordion?.()
              }}
            >
              <img
                src="/images/chevron-down-icon.svg"
                className={cn('transition-transform duration-200', isOpen ? '' : '-rotate-90')}
                alt="open"
              />
            </div>
            <p className="font-EN-KR-caption-bold text-contents-onDarkPrimary">{points}</p>
          </div>
        )}
      </div>
      <div className="flex gap-1">
        <span className={cn('py-[5px] px-2', commonOverlayStyle, getDividerColor())}>
          <p className="font-EN-KR-caption-bold text-contents-onDarkPrimary">{`${number} • ${formatDateRange(new Date(start), new Date(finish))}`}</p>
        </span>
        {type !== 'done' && isTeamStrengthEditMode ? (
          <Input
            id={`team-strength-${number}`}
            type="number"
            aria-label="team strength"
            className="font-EN-KR-body2-regular w-[58px] h-[24px] text-contents-primary rounded-xs"
            value={teamStrength}
            min={0}
            max={1000}
            onChange={(e) => {
              const value = Math.trunc(Number.parseInt(e.target.value, 10))
              if (e.target.value.startsWith('-') || value > 1000) return
              setTeamStrength(Number.isNaN(value) ? '' : value.toString())
            }}
            onClick={(e) => e.stopPropagation()}
            onBlur={updateTeamStrength}
            onKeyUp={(e) => {
              if (e.key === 'Enter') updateTeamStrength()
              if (e.key === 'Escape') {
                setTeamStrength((initialTeamStrength * 100).toString())
                setIsTeamStrengthEditMode(false)
              }
            }}
          />
        ) : (
          <div
            className={cn(
              'py-2 px-1 flex justify-center items-center',
              commonOverlayStyle,
              getDividerColor(),
              type === 'done' ? 'cursor-default' : 'cursor-pointer',
            )}
            onClick={(e) => {
              e.stopPropagation()
              setIsTeamStrengthEditMode(true)
              setTimeout(() => document.getElementById(`team-strength-${number}`)?.focus())
            }}
            onKeyDown={(e) => e.preventDefault()}
            onKeyUp={(e) => {
              if (e.key === ' ') {
                setIsTeamStrengthEditMode(true)
                setTimeout(focusTeamStrengthInput)
              }
            }}
          >
            <div className="flex items-center gap-1">
              <img src="/images/team-strength-icon.svg" alt="team strength" />
              <Label
                htmlFor={`team-strength-${number}`}
                className="font-EN-KR-caption-bold text-contents-onDarkPrimary"
                tabIndex={0}
              >
                {Math.round(initialTeamStrength * 100)}%
              </Label>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
